// src/pages/commercial/CommercialDealsPage.js
import React, { useEffect, useState } from 'react';
import CommercialLayout from '../../components/commercial/CommercialLayout';
import { useAuth } from '../../contexts/AuthContext';
import pb from '../../lib/pocketbase';

const DEAL_STAGES = {
    'new': { label: 'Nouveau / Prospect', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)', border: 'rgba(59, 130, 246, 0.2)' },
    'proposal': { label: 'Prop. en cours', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.2)' },
    'negotiation': { label: 'Négociation', color: '#ec4899', bg: 'rgba(236, 72, 153, 0.1)', border: 'rgba(236, 72, 153, 0.2)' },
    'won': { label: 'Gagné', color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)', border: 'rgba(16, 185, 129, 0.2)' },
    'lost': { label: 'Perdu', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.2)' }
};

export default function CommercialDealsPage() {
    const { user } = useAuth();
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal State (Create Customer)
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: ''
    });
    const [formLoading, setFormLoading] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [customersRes, quotesRes] = await Promise.all([
                pb.collection('customers').getFullList({
                    sort: '-created',
                    filter: `created_by = "${user.id}" || commercial_id = "${user.id}"`
                }),
                pb.collection('quotes').getFullList({
                    sort: '-created',
                    filter: `customer_id.commercial_id = "${user.id}"` // Safety filter
                })
            ]);

            // Process Logic: Client = Deal
            const processedDeals = customersRes.map(client => {
                const clientQuotes = quotesRes.filter(q => q.customer_id === client.id);

                let derivedStatus = 'new';
                let dealAmount = 0;

                // Amount Logic: Sum of meaningful quotes (e.g. Sent, Accepted)
                // If Accepted exists, value is Accepted amount. 
                // Else sent amount.
                const acceptedQuotes = clientQuotes.filter(q => q.status === 'accepted');
                const activeQuotes = clientQuotes.filter(q => ['sent', 'draft'].includes(q.status));

                if (acceptedQuotes.length > 0) {
                    derivedStatus = 'won';
                    dealAmount = acceptedQuotes.reduce((acc, q) => acc + (q.total || 0), 0);
                } else if (clientQuotes.some(q => q.status === 'sent')) {
                    derivedStatus = 'negotiation';
                    dealAmount = activeQuotes.reduce((acc, q) => acc + (q.total || 0), 0);
                } else if (clientQuotes.some(q => q.status === 'draft')) {
                    derivedStatus = 'proposal';
                    dealAmount = activeQuotes.reduce((acc, q) => acc + (q.total || 0), 0);
                } else if (clientQuotes.some(q => q.status === 'rejected')) {
                    // Only lost if ALL are rejected and no new ones? 
                    // Let's say if last one is rejected
                    derivedStatus = 'lost';
                }

                return {
                    id: client.id,
                    title: client.name + (client.company ? ` (${client.company})` : ''),
                    amount: dealAmount,
                    status: derivedStatus,
                    created: client.created,
                    clientData: client,
                    quoteCount: clientQuotes.length
                };
            });

            setDeals(processedDeals);

        } catch (err) {
            console.error("Error loading data", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [user.id]);

    const handleCreateClient = async (e) => {
        e.preventDefault();
        setFormLoading(true);
        try {
            await pb.collection('customers').create({
                ...formData,
                created_by: user.id,
                commercial_id: user.id,
                status: 'prospect'
            });
            setShowModal(false);
            setFormData({ name: '', email: '', phone: '', company: '' });
            fetchData();
        } catch (err) {
            console.error("Error creating client", err);
            alert("Erreur lors de la création du client.");
        } finally {
            setFormLoading(false);
        }
    };

    // Columns derivation
    const columns = Object.keys(DEAL_STAGES).map(stageKey => {
        return {
            key: stageKey,
            ...DEAL_STAGES[stageKey],
            items: deals.filter(d => d.status === stageKey)
        };
    });

    return (
        <CommercialLayout title="">
            <div className="commercial-page-wrapper" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <style>{`
                 /* Scoped Layout Styles */
                .commercial-page-wrapper {
                    --admin-panel: var(--commercial-panel, #fff);
                    --admin-panel-2: var(--commercial-panel-2, #f9fafb);
                    --admin-border: var(--commercial-border, #e2e8f0);
                    --admin-text: var(--commercial-text, #0f172a);
                    --admin-muted: var(--commercial-muted, #64748b);
                    --admin-accent: var(--commercial-accent, #4f46e5);
                }
                .commercial-page-wrapper .btn-primary { 
                    display: inline-flex; align-items: center; gap: 10px;
                    padding: 10px 14px; border-radius: 10px;
                    background: linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85));
                    border: 1px solid rgba(79,70,229,0.6);
                    box-shadow: 0 6px 18px rgba(79,70,229,0.35);
                    color: white; font-weight: 500; cursor: pointer;
                }
                .commercial-page-wrapper .kanban-board { display: flex; gap: 16px; overflow-x: auto; height: 100%; padding-bottom: 20px; align-items: flex-start; }
                .commercial-page-wrapper .kanban-column { min-width: 280px; width: 320px; background: var(--admin-panel-2); border-radius: 12px; border: 1px solid var(--admin-border); display: flex; flex-direction: column; max-height: 100%; }
                .commercial-page-wrapper .kanban-column-header { padding: 16px; font-weight: 600; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid var(--admin-border); background: var(--admin-panel); border-radius: 12px 12px 0 0; color: var(--admin-text); }
                .commercial-page-wrapper .count-badge { background: var(--admin-border); padding: 2px 8px; border-radius: 99px; font-size: 0.75rem; color: var(--admin-text); }
                .commercial-page-wrapper .kanban-column-content { padding: 12px; display: flex; flex-direction: column; gap: 12px; overflow-y: auto; flex: 1; }
                
                .commercial-page-wrapper .deal-card { 
                    background: var(--admin-panel); border: 1px solid var(--admin-border); border-radius: 8px; padding: 12px; 
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05); cursor: default; display: flex; flex-direction: column; gap: 8px; color: var(--admin-text);
                }
                .commercial-page-wrapper .deal-title { font-weight: 600; font-size: 0.95rem; display: flex; align-items: center; gap: 6px; }
                .commercial-page-wrapper .deal-amount { font-weight: 700; color: var(--admin-accent); font-size: 1rem; }
                .commercial-page-wrapper .deal-meta { display: flex; justify-content: space-between; align-items: center; margin-top: 4px; padding-top: 8px; border-top: 1px dashed var(--admin-border); font-size: 0.8rem; color: var(--admin-muted); }
                
                /* Modal */
                .commercial-page-wrapper .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1001; backdrop-filter: blur(2px); }
                .commercial-page-wrapper .modal-content { background: var(--admin-panel); color: var(--admin-text); border: 1px solid var(--admin-border); border-radius: 16px; width: 100%; max-width: 500px; padding: 24px; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); }
                .commercial-page-wrapper .form-control { width: 100%; padding: 10px 12px; border-radius: 8px; border: 1px solid var(--admin-border); background: var(--admin-panel-2); color: var(--admin-text); margin-bottom: 12px; }
                `}</style>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '1.8rem' }}>Affaires en cours</h1>
                        <p style={{ color: 'var(--admin-muted)', margin: '4px 0 0 0' }}>Vos clients et leur avancement (Automatisé)</p>
                    </div>
                    <button className="btn-primary" onClick={() => setShowModal(true)}>
                        <span>+</span>
                        <span>Nouveau Client</span>
                    </button>
                </div>

                {loading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: 'var(--admin-muted)' }}>Chargement...</div>
                ) : (
                    <div className="kanban-board">
                        {columns.map(col => (
                            <div key={col.key} className="kanban-column">
                                <div className="kanban-column-header" style={{ borderTop: `4px solid ${col.color}` }}>
                                    <span>{col.label}</span>
                                    <span className="count-badge">{col.items.length}</span>
                                </div>
                                <div className="kanban-column-content">
                                    {col.items.map(deal => (
                                        <div key={deal.id} className="deal-card">
                                            <div className="deal-title">
                                                <span>👤</span>
                                                {deal.title}
                                            </div>
                                            <div style={{ fontSize: '0.85rem', color: 'var(--admin-muted)' }}>
                                                {deal.quoteCount > 0 ? `${deal.quoteCount} devis associé(s)` : 'Aucun devis'}
                                            </div>
                                            {deal.amount > 0 && (
                                                <div className="deal-amount">
                                                    {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(deal.amount)}
                                                </div>
                                            )}
                                            <div className="deal-meta">
                                                <span>Depuis le {new Date(deal.created).toLocaleDateString()}</span>
                                                <span style={{ fontSize: '0.75rem', padding: '2px 6px', background: col.bg, color: col.color, borderRadius: 4 }}>
                                                    {col.label}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                    {col.items.length === 0 && (
                                        <div style={{ textAlign: 'center', padding: '20px', color: 'var(--admin-muted)', opacity: 0.6, fontSize: '0.85rem' }}>
                                            Aucun client
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {showModal && (
                    <div className="modal-overlay" onClick={() => setShowModal(false)}>
                        <div className="modal-content" onClick={e => e.stopPropagation()}>
                            <h3 style={{ marginTop: 0 }}>Nouveau Client / Prospect</h3>
                            <form onSubmit={handleCreateClient}>
                                <input placeholder="Nom du contact" className="form-control" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                                <input placeholder="Entreprise (Optionnel)" className="form-control" value={formData.company} onChange={e => setFormData({ ...formData, company: e.target.value })} />
                                <input placeholder="Email" type="email" className="form-control" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
                                <input placeholder="Téléphone" className="form-control" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />

                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                                    <button type="button" onClick={() => setShowModal(false)} style={{ background: 'transparent', border: '1px solid var(--admin-border)', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', color: 'var(--admin-text)' }}>Annuler</button>
                                    <button type="submit" className="btn-primary" disabled={formLoading}>{formLoading ? '...' : 'Créer'}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </CommercialLayout>
    );
}
